import { useEffect, useMemo, useRef } from 'react'
import { useLocation, Location, matchPath, useNavigate } from 'react-router-dom'
import { routes, overlayRoutes, invalidOverlayBackdropRoutes } from '../../routes'
import useStableCallback from '../shared/useStableCallback'

const lettersLocation: Location = {
  key: 'companyPlanningRecordLetters',
  pathname: routes.companyPlanningRecordLetters,
  search: '',
  hash: '',
  state: null,
}
const planningRecordsLocation: Location = {
  key: 'planningRecords',
  pathname: routes.planningRecords,
  search: '',
  hash: '',
  state: null,
}
const blankLocation: Location = {
  key: 'blank',
  pathname: routes.blank,
  search: '',
  hash: '',
  state: null,
}
const homeLocation: Location = {
  key: 'default',
  pathname: routes.home,
  search: '',
  hash: '',
  state: null,
}
const defaultBackMap: Record<(typeof overlayRoutes)[number]['defaultBack'], Location> = {
  '/': homeLocation,
  '/letters': lettersLocation,
  '/planning-records': planningRecordsLocation,
}
const defaultBackdropMap: Record<(typeof overlayRoutes)[number]['defaultBackdrop'], Location> = {
  '/': homeLocation,
  '/blank': blankLocation,
}
let backgroundLocation: Location<any> | undefined
const getPersistedBackgroundLocation = () => {
  if (backgroundLocation) {
    return backgroundLocation
  }
  const value = sessionStorage.getItem('lastBackgroundLocation')
  return value ? (JSON.parse(value) as Location<any>) : undefined
}
const setPersistedBackgroundLocation = (location: Location<any>) => {
  backgroundLocation = location
  setTimeout(() => {
    sessionStorage.setItem('lastBackgroundLocation', JSON.stringify(location))
  })
}
const useMatchOverlayPath = () => {
  const location = useLocation()
  const match = useMemo(() => {
    let match: (typeof overlayRoutes)[number] | undefined = undefined
    for (const overlayRoute of overlayRoutes) {
      const isOverlay = !!matchPath(overlayRoute.path, location.pathname)
      if (isOverlay) {
        match = overlayRoute
        break
      }
    }
    return match
  }, [location.pathname])
  return match
}
export const useOverlayPageNavigateBack = () => {
  const navigate = useNavigate()
  const match = useMatchOverlayPath()
  const navigateBack = useStableCallback(() => {
    const prevLocation = getPersistedBackgroundLocation()
    if (prevLocation) {
      navigate(prevLocation, { replace: false })
    } else if (match) {
      navigate(defaultBackMap[match.defaultBack])
    } else {
      navigate(homeLocation)
    }
  })
  return navigateBack
}
export const usePersistedBackgroundLocation = () => {
  const location = useLocation()
  const prevLocationRef = useRef<typeof location | undefined>(getPersistedBackgroundLocation())
  const match = useMatchOverlayPath()
  useEffect(() => {
    if (
      !match &&
      !invalidOverlayBackdropRoutes.includes(
        location.pathname as (typeof invalidOverlayBackdropRoutes)[number],
      )
    ) {
      setPersistedBackgroundLocation(location)
      prevLocationRef.current = location
    }
  }, [match, location])
  const routingLocation = match
    ? prevLocationRef.current || defaultBackdropMap[match.defaultBackdrop]
    : location
  return routingLocation
}
